import config from 'src/shared/config';
import axios from 'src/shared/utils/axios';
import { toLocalDateWithFormat } from 'src/shared/utils/localDateTime';
import DummyApi from '../dummyApi';
import { ApprovalDto, ApprovalType, ApproverDto, LimitType, StatusType } from './approvalApi';
import { RuleApproverDto } from './ruleDetailApi';
const FXM_API_ENABLED = config.servers.fxm.enabled;
const FXM_API_URL = config.servers.fxm.url;
export enum FileType {
  NORMAL = 29,
  FED5_FSD = 1150,
  FED5_FSN = 1151,
  FED5_FSE = 1152,
  FED5_NX = 1153,
  FED5_WRAPSODY = 1154,
  ECO = 1155,
  FDR_S = 1156,
  FDR_N = 1159,
  FED4_FSN = 103,
  FED4_FSD = 26,
  FED4_FSE = 105,
  FED4_WRAPSODY = 115,
  INCAPS = 104,
  MARANY = 101,
  SOFTCAMP = 1192,
}
export interface ApplyStatusDto {
  startDate: number;
  endDate: number;
  approvalNo: string;
  status: string;
  approvalMethod: string;
  approvalType: string;
}
export interface ScreenApplyStatusDto extends ApplyStatusDto {
  exceptTargetList: string[];
}
export interface PrintPolicyApplyStatusDto extends ApplyStatusDto {
  watermarkPrint: string; // 적용 범위 > 워터마크 예외(Y/N)
  detectionPrint: string; // 적용 범위 > 중요 정보 검출(Y/N)
}
export interface MethodDto {
  pre: boolean; // 사전 승인
  post: boolean; // 사후 승인
  self: boolean; // 자가 승인
}
export interface ProcessDto {
  code: string; // 적용 대상 프로그램 고유 코드
  value: string; // 적용 대상 프로그램 이름
}
export interface AllowedFileTypeDto {
  dsdCode: string; // 문서 고유 dsdcode
  fileType: number; // 파일 타입 : FileType
  allowed: boolean; // 전체 허용 유무
}
export interface SecurityLevelDto {
  fileType: number; // 파일 타입 : FileType
  dsdCode: string; // 문서의 고유 dsdCode
  code: string; // 문서의 고유 code (N 이면 문서 등급 코드, D 면 시스템 코드, W 면 태그 코드, NX 면 NX)
  name: string; // 코드별 이름
}
export interface TargetScopeDto {
  method: MethodDto; //신청 시 선택 가능 승인 방법
  allAllowedFileTypeEx: AllowedFileTypeDto[]; // 전체 허용 대상 문서 유형
  securityLevelList: SecurityLevelDto[]; // 허용 가능 범위, 문서 유형별 특정 코드s
  allowDownloadCount: number; // 신청건에 대한 승인 완료 후 개별 파일에 대한 문서 다운로드 허용 회수, -1일 시 무제한
  approver: RuleApproverDto[];
}
export interface PrintFileTargetScopeDto extends TargetScopeDto {
  allowDownloadCount: number; // 파일당 인쇄 허용 횟수
  targetList: string[]; // 적용 범위  WATERMARK_PRINT: 워터마크 예외, DETECTION_PRINT: 중요 정보 검출
  allowedScopeList: string[]; // 사용 방법 PERIOD : 기간  COUNT : 파일당 인쇄 허용 횟수
  useAppPrint: string; // 어플리케이션으로 인쇄 유무(Y/N)
  allowedDay: number; // 신청 가능 최대 일수
  allowedPrintCount: number; //  파일당 인쇄 허용 횟수
}
interface ApproverValidDto {
  error: null | string;
  errorResArgs: null | string;
  errorData: null | string;
}
export const defaultTargetScopeDto = (): TargetScopeDto => {
  return {
    method: {
      pre: false,
      post: false,
      self: false,
    },
    allAllowedFileTypeEx: [],
    securityLevelList: [],
    approver: [],
    allowDownloadCount: -1,
  };
};
export interface PrimaryExceedDto {
  [key: string]: boolean;
}
export interface PrimaryDto {
  code: string; // 정책 고유 코드
  approvalType:
    | 'TAKEOUT'
    | 'SCREEN'
    | 'DECRYPT'
    | 'PROVISIONAL'
    | 'PRINTPOLICY'
    | 'PRINTFILE'
    | 'PRINTCLIENT'; // 결재 유형
  name: string; // 정책 이름
  description: string; // 정책 설명
  status: 'ACTIVE' | 'INACTIVE'; // 정책 상태
  startTs: number; // 정책 유효 기간 - 시작, 각각 -1일 시 무제한
  endTs: number; // 정책 유효 기간 - 종류, 각각 -1일 시 무제한
  limitUnit: 'LIMIT' | 'DAY' | 'WEEK' | 'MONTH'; // 신청 가능 횟수 단위
  limitApply: number; //신청 가능 단위에 맞춘 횟수, -1일 시 무제한
}
export interface UserRuleDto {
  takeout: ApplyStatusDto | null; // PC 반출 상태, null이면 미적용
  exceptScreen: ScreenApplyStatusDto | null; // 화면 보안 예외 적용 상태, null이면 미적용
  printPolicy: PrintPolicyApplyStatusDto | null;
  primary: PrimaryDto[] | null; // 적용된 정책 list, 없을 시 []
  proxys: string | null; // 나에게 지정된 대리 승인
  existApproval: boolean; // 한개 이상의 승인/반려를 진행했는지 true/false
  isPrimaryExceed: PrimaryExceedDto[] | null; //로그인한 사용자가 정책을 사용한 횟수(list)
  existPenddingApproval: boolean; // 현재 내가 결재할 결재건이 있는지 true/false
  proxyApprover: boolean; //현재 대리 승인자로 지정되어 있는지 true/false
  approver: boolean; // 승인 단계 설정 중 하나라도, 승인자로 적용되어있는지 true/false
}
interface UserRuleApi {
  getRule(): Promise<UserRuleDto>;
  getRuleWithToken(token: string): Promise<UserRuleDto>;
  checkApproverValid(token: string): Promise<boolean>;
}
class UserRuleServerApi implements UserRuleApi {
  async getRule(): Promise<UserRuleDto> {
    const resp = await axios.get<UserRuleDto>(`${FXM_API_URL}/api/rule`);
    return resp.data;
  }
  async getRuleWithToken(token: string): Promise<UserRuleDto> {
    const resp = await axios.get<UserRuleDto>(`${FXM_API_URL}/api/rule`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return resp.data;
  }
  async checkApproverValid(token: string): Promise<boolean> {
    const resp = await axios.get<ApproverValidDto>(`${FXM_API_URL}/api/rule/valid`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return resp.data.error ? false : true;
  }
}
const userRuleApi = new UserRuleServerApi();
export default userRuleApi;
